import type { PublicRecap, RecapCta, InvitationPreview, RecapFunnelEventPayload, Reaction, ReactionSummary, StoryBrief, ThreadPhaseInventory } from './types';
import { createClientTraceContext, rememberResponseTrace } from '../lib/traceContext';
import type { PushTopic } from '../lib/pushManager';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v2';

interface FetchOptions extends RequestInit {
  skipCsrf?: boolean;
  suppressAuthRedirect?: boolean;
  // Telemetry posts set this so their own response trace doesn't overwrite the
  // last *user-action* trace. Otherwise the next captured event would stitch to
  // the telemetry-ingestion trace instead of the action that triggered it.
  skipTraceCapture?: boolean;
}

/**
 * 4xx codes that mean something is genuinely wrong rather than "the user typed
 * the wrong thing": auth broke, we were rate-limited, the payload was rejected.
 * 404/400/409 are ordinary traffic on this client and would drown the signal.
 */
const UNEXPECTED_CLIENT_STATUSES = new Set([403, 413, 429]);

/**
 * Collapse identifier segments so api_error groups by ENDPOINT, not by row —
 * and so invite tokens and share tokens never reach the telemetry payload.
 */
export function pathTemplate(path: string): string {
  return path
    .split('?')[0]
    .replace(/\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g, '/:id')
    .replace(/\/[A-Za-z0-9_-]{16,}/g, '/:token');
}

/**
 * Emit through captureAnalytics without importing it at module scope —
 * captureAnalytics imports apiFetch from THIS module, so a static import would
 * close a cycle. Failures here are swallowed: telemetry must never be able to
 * break a request path.
 */
function reportApiEvent(
  event: 'api_error' | 'session_lost',
  metadata: Record<string, unknown>,
  options: { beacon?: boolean } = {},
): void {
  void import('../lib/captureAnalytics')
    .then(({ trackCaptureEvent, flushEvents }) => {
      trackCaptureEvent(event, metadata);
      if (options.beacon) flushEvents({ useBeacon: true });
    })
    .catch(() => { /* telemetry is best-effort */ });
}

let csrfToken: string | null = null;

export function setCsrfToken(token: string): void {
  csrfToken = token;
}

export function clearCsrfToken(): void {
  csrfToken = null;
}

function getCsrfToken(): string | null {
  if (csrfToken) return csrfToken;
  // Read from csrf_token cookie (set by backend on guest session creation)
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  if (match) {
    csrfToken = decodeURIComponent(match[1]);
  }
  return csrfToken;
}

// Returns the raw Response for non-204 success cases. Returns null on 401
// (after triggering session cleanup + redirect) so callers see the same
// silent-undefined behavior as before. Throws ApiError for other failures.
async function rawFetch(path: string, options: FetchOptions): Promise<Response | null> {
  const { skipCsrf = false, suppressAuthRedirect = false, skipTraceCapture = false, headers: customHeaders, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(customHeaders as Record<string, string> ?? {}),
  };
  const traceContext = createClientTraceContext();
  headers.Traceparent = headers.Traceparent ?? traceContext.traceparent;
  headers['X-Request-Id'] = headers['X-Request-Id'] ?? traceContext.requestId;

  if (!skipCsrf && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(fetchOptions.method ?? '')) {
    const token = getCsrfToken();
    if (token) {
      headers['X-CSRF-Token'] = token;
    }
  }

  const response = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...fetchOptions,
    headers,
  });
  if (!skipTraceCapture) {
    rememberResponseTrace(
      response.headers?.get('Traceparent') ?? headers.Traceparent,
      response.headers?.get('X-Request-Id') ?? headers['X-Request-Id'],
    );
  }

  if (response.ok) return response;

  if (response.status === 401 && !suppressAuthRedirect) {
    clearCsrfToken();
    localStorage.removeItem('guest_session');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    // Beacon BEFORE the hard navigation — a fetch started here would be aborted
    // by it, and this is the one event that explains why a guest suddenly found
    // themselves back on the landing page mid-contribution.
    if (!path.startsWith('/telemetry/')) reportApiEvent('session_lost', {
      surface: 'api',
      path: pathTemplate(path),
      method: fetchOptions.method ?? 'GET',
      requestId: headers['X-Request-Id'],
    }, { beacon: true });
    window.location.href = '/';
    return null;
  }

  const error = await response.json().catch(() => ({
    error: { code: 'UNKNOWN', message: response.statusText },
  }));

  // 5xx, plus the 4xx classes the client cannot resolve by retrying. Ordinary
  // validation/not-found noise is excluded so the signal stays readable.
  // requestId is directly joinable to the server-side trace.
  // NEVER report the telemetry endpoint's own failures: trackCaptureEvent
  // schedules a flush, whose failure would emit another api_error, which
  // schedules another flush — a self-sustaining loop that defeats the 15s
  // backoff and grows the queue until it hits the cap.
  const isTelemetryPath = path.startsWith('/telemetry/');
  if (!isTelemetryPath && (response.status >= 500 || UNEXPECTED_CLIENT_STATUSES.has(response.status))) {
    reportApiEvent('api_error', {
      surface: 'api',
      path: pathTemplate(path),
      method: fetchOptions.method ?? 'GET',
      status: response.status,
      code: error.error?.code ?? 'UNKNOWN',
      requestId: response.headers?.get('X-Request-Id') ?? headers['X-Request-Id'],
    });
  }

  throw new ApiError(response.status, error.error?.code ?? 'UNKNOWN', error.error?.message ?? 'Request failed');
}

export async function apiFetch<T>(
  path: string,
  options: FetchOptions = {},
): Promise<T> {
  const response = await rawFetch(path, options);
  if (response === null || response.status === 204) return undefined as T;
  const json = await response.json();
  return json.data ?? json;
}

/**
 * Like apiFetch but returns the raw JSON body without unwrapping `json.data`.
 * Use for paginated list endpoints where `data` is a structural field (not a wrapper).
 */
export async function apiFetchRaw<T>(
  path: string,
  options: FetchOptions = {},
): Promise<T> {
  const response = await rawFetch(path, options);
  if (response === null || response.status === 204) return undefined as T;
  return response.json();
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Media upload via presigned URL flow
interface PresignedUrlResponse {
  uploadUrl: string;
  mediaUrl: string;
  key: string;
}

// Generic media upload via presigned URL flow (works for audio, video, photo)
export async function uploadMedia(
  threadId: string,
  blob: Blob,
  filename: string,
  contentType: string,
  signal?: AbortSignal,
): Promise<string> {
  // Step 1: Request presigned URL from backend
  const presigned = await apiFetch<PresignedUrlResponse>(
    `/threads/${threadId}/media/presigned-url`,
    {
      method: 'POST',
      body: JSON.stringify({ filename, contentType }),
      signal,
    },
  );

  // Step 2: Upload file directly to S3 via presigned URL
  const uploadResponse = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: blob,
    signal,
  });

  if (!uploadResponse.ok) {
    throw new ApiError(
      uploadResponse.status,
      'UPLOAD_FAILED',
      `S3 upload failed: ${uploadResponse.statusText}`,
    );
  }

  // Step 3: Return the mediaUrl for use in contribution creation
  return presigned.mediaUrl;
}

// Media upload with XMLHttpRequest for real-time progress reporting
export async function uploadMediaWithProgress(
  threadId: string,
  blob: Blob,
  filename: string,
  contentType: string,
  onProgress: (progress: number) => void,
): Promise<string> {
  // Step 1: Request presigned URL from backend (same as uploadMedia)
  const presigned = await apiFetch<PresignedUrlResponse>(
    `/threads/${threadId}/media/presigned-url`,
    {
      method: 'POST',
      body: JSON.stringify({ filename, contentType }),
    },
  );

  // Step 2: Upload with XMLHttpRequest for progress events
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presigned.uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(e.loaded / e.total);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('Upload network error'));
    xhr.send(blob);
  });

  return presigned.mediaUrl;
}

// Photo upload — delegates to uploadMedia (kept for backward compatibility)
export async function uploadPhoto(
  threadId: string,
  file: Blob,
  filename: string,
  contentType: string,
  signal?: AbortSignal,
): Promise<string> {
  return uploadMedia(threadId, file, filename, contentType, signal);
}

// Guest session API
export const guestApi = {
  createSession: (invitationToken: string, displayName: string) =>
    apiFetch<{ sessionId: string; sessionToken: string; spaceId: string; spaceName: string; threadId: string | null; accessScope: string; phaseIndex?: number | null; expiresAt: string; csrfToken: string }>(
      '/guest/session',
      { method: 'POST', body: JSON.stringify({ invitationToken, displayName }), skipCsrf: true },
    ),

  createSessionFromShortCode: (shortCode: string, displayName: string) =>
    apiFetch<{ sessionId: string; sessionToken: string; spaceId: string; spaceName: string; threadId: string | null; accessScope: string; phaseIndex?: number | null; expiresAt: string; csrfToken: string }>(
      '/guest/session/code',
      { method: 'POST', body: JSON.stringify({ shortCode, displayName }), skipCsrf: true },
    ),

  createSessionFromPhaseInvite: (invitationToken: string, displayName: string) =>
    apiFetch<{ sessionId: string; sessionToken: string; spaceId: string; spaceName: string; threadId: string | null; accessScope: string; phaseIndex?: number | null; expiresAt: string; csrfToken: string }>(
      '/guest/session/phase-invite',
      { method: 'POST', body: JSON.stringify({ invitationToken, displayName }), skipCsrf: true },
    ),

  convert: (email: string, password: string, displayName: string) =>
    apiFetch<{ accessToken: string; refreshToken: string; user: Record<string, unknown> }>(
      '/guest/convert',
      { method: 'POST', body: JSON.stringify({ email, password, displayName }) },
    ),

  // Revoke the current guest session server-side (W7). Best-effort — callers
  // still clear local state regardless of the outcome.
  logout: () =>
    apiFetch<{ success: boolean }>(
      '/guest/session/logout',
      { method: 'POST', body: '{}', suppressAuthRedirect: true },
    ),

  /**
   * Record (or withdraw, with `email: null`) the address for the one
   * finished-moment message. Only the consent VERSION is sent — the sentence
   * itself is stored server-side, so the audit record can't be self-reported.
   */
  setOutcomeContact: (body: { email: string | null; consentVersion: string }) =>
    apiFetch<{ notifyEmail: string | null; notifyConsentAt: string | null }>(
      '/guest/session/outcome-contact',
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  getSessionMetadata: (options: FetchOptions = {}) =>
    apiFetch<{
      sessionId: string;
      sessionToken: string;
      spaceId: string;
      spaceName: string;
      threadId: string | null;
      displayName: string;
      expiresAt: string;
    }>('/guest/session/me', options),

  // Resume requires proof-of-possession of the session's secret token (W4).
  // The token is presented via the X-Guest-Token header (and body for redundancy);
  // the sessionId in the path is only cross-checked, no longer a credential.
  //
  // suppressAuthRedirect: a dead resume entry now answers 401 (INVALID_SESSION /
  // SESSION_EXPIRED / SESSION_REVOKED / SESSION_MISMATCH — W4 replaced the old
  // 404 SESSION_NOT_FOUND). The default 401 handling in rawFetch would swallow
  // that into `undefined`, hard-navigate to '/', and take the caller's *live*
  // session down with it — so the caller could never prune the dead entry. Here
  // the 401 is expected and diagnostic; HomePage handles it itself.
  activateSession: (sessionId: string, sessionToken: string) =>
    apiFetch<{
      sessionToken: string;
      sessionId: string;
      spaceId: string;
      spaceName: string;
      threadId: string | null;
      accessScope: string;
      displayName: string;
      expiresAt: string;
      csrfToken: string;
    }>(`/guest/session/activate/${sessionId}`, {
      method: 'POST',
      body: JSON.stringify({ sessionToken }),
      skipCsrf: true,
      suppressAuthRedirect: true,
      headers: { 'X-Guest-Token': sessionToken },
    }),

  /**
   * The share token for a thread's story-conclusion reel, or null when there
   * isn't a playable one yet. Lets a completed thread offer the reel on a cold
   * load, where the render event that carried the token is long gone.
   */
  getThreadHighlightToken: (threadId: string) =>
    apiFetch<{ shareToken: string | null }>(
      `/threads/${encodeURIComponent(threadId)}/highlight`,
    ),

  /**
   * The thread's phase inventory: every phase in canonical template order with
   * the prompts contributors actually answered there. Powers canonical phase
   * ordering + prompt headers on the thread feed.
   */
  getThreadPhases: (threadId: string) =>
    apiFetch<ThreadPhaseInventory>(
      `/threads/${encodeURIComponent(threadId)}/phases`,
    ),

  /**
   * The "Catch Me Up" brief for a returning contributor: story-so-far, phase
   * goal, current prompt, a "where things stand" line, and the personalized
   * "since your last visit" delta.
   */
  getStoryBrief: (threadId: string) =>
    apiFetch<StoryBrief>(
      `/threads/${encodeURIComponent(threadId)}/story-brief`,
    ),

  /**
   * Advance the caller's read cursor to the snapshot they were actually shown.
   * Pass the brief's `observedThrough`; content that arrived after that instant
   * is preserved for the next visit.
   *
   * Call on an explicit user action — jump in, dismiss, a submitted take —
   * NEVER on brief-fetch success, which would clear a delta the reader has not
   * looked at yet.
   */
  markThreadRead: (threadId: string, observedThrough?: string) =>
    apiFetch<{ lastReadAt: string }>(
      `/threads/${encodeURIComponent(threadId)}/mark-read`,
      {
        method: 'POST',
        body: JSON.stringify(observedThrough ? { observedThrough } : {}),
      },
    ),
};

// Public API
export const publicApi = {
  getSpaceIntro: (spaceId: string) =>
    apiFetch<{ spaceName: string; threadCount: number; memberCount: number }>(
      `/public/spaces/${spaceId}/intro`,
      { skipCsrf: true },
    ),

  getRecapByShareToken: (shareToken: string) =>
    apiFetch<{ recap: PublicRecap; cta: RecapCta }>(
      `/recaps/public/${encodeURIComponent(shareToken)}`,
      { skipCsrf: true },
    ),

  // Full URL for the <track src> (P1.6). Resolves to
  // /api/v2/recaps/public/:shareToken/captions.vtt, which the existing /api Vite
  // dev proxy already routes — no vite.config.ts change needed.
  recapCaptionsUrl: (shareToken: string): string =>
    `${API_BASE}/recaps/public/${encodeURIComponent(shareToken)}/captions.vtt`,

  trackRecapFunnelEvent: (shareToken: string, payload: RecapFunnelEventPayload) =>
    apiFetch<void>(
      `/recaps/public/${encodeURIComponent(shareToken)}/funnel-event`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
        skipCsrf: true,
      },
    ),

  trackRecapInstall: (shareToken: string) =>
    apiFetch<void>(
      `/recaps/public/${encodeURIComponent(shareToken)}/install-attribution`,
      { method: 'POST', skipCsrf: true },
    ),

  getInvitationPreview: (token: string) =>
    apiFetch<InvitationPreview>(
      `/public/invitations/${encodeURIComponent(token)}/preview`,
      { skipCsrf: true },
    ),

  getCodePreview: (code: string) =>
    apiFetch<InvitationPreview>(
      `/public/codes/${encodeURIComponent(code)}/preview`,
      { skipCsrf: true },
    ),

  getPhaseInvitePreview: (token: string) =>
    apiFetch<InvitationPreview>(
      `/public/phase-invites/${encodeURIComponent(token)}/preview`,
      { skipCsrf: true },
    ),
};

// Template state mutations (PWA-only V2 routes; see Docs/13_OURTAKE_REBRAND.md
// for template ↔ display-name mapping). Goes through apiFetch so CSRF is
// auto-attached. Errors propagate as ApiError with the backend's `code`.
//
// There is deliberately no `selectArc` here. `POST /threads/:id/template/arc`
// still exists server-side, but it is gated on thread participation, which a
// space-scoped guest does not have before their first take (#545). The PWA locks
// the Mood Ring arc by carrying `arcShape` on the contribution itself, so the
// arc is never locked unless someone actually contributes.
export const templateApi = {
  assignRole: (threadId: string, role: string) =>
    apiFetch<{ message: string; role: string }>(
      `/threads/${threadId}/template/roles`,
      { method: 'POST', body: JSON.stringify({ role }) },
    ),
};

// Contribution reactions (PWA-only V2 routes; guests can react via guestOrAuth).
// CSRF is auto-attached on POST/DELETE. `summary` is the read path the feed uses;
// `list` backs the toggle-off reaction-id lookup when the id isn't cached locally.
export const reactionApi = {
  add: (threadId: string, contributionId: string, reactionType: string) =>
    apiFetch<Reaction>(
      `/threads/${threadId}/reactions`,
      { method: 'POST', body: JSON.stringify({ contributionId, reactionType }) },
    ),

  remove: (threadId: string, reactionId: string) =>
    apiFetch<{ message: string }>(
      `/threads/${threadId}/reactions/${reactionId}`,
      { method: 'DELETE' },
    ),

  // Raw envelope: `data` is the structural array, plus cursor/hasMore for paging.
  list: (threadId: string, contributionId?: string) =>
    apiFetchRaw<{ data: Reaction[]; cursor: string | null; hasMore: boolean }>(
      contributionId
        ? `/threads/${threadId}/reactions?contributionId=${encodeURIComponent(contributionId)}`
        : `/threads/${threadId}/reactions`,
    ),

  // Returns just the keyed rollup (unwraps the { data } envelope; {} on empty).
  summary: (threadId: string) =>
    apiFetchRaw<{ data: ReactionSummary }>(
      `/threads/${threadId}/reactions/summary`,
    ).then((res) => res?.data ?? {}),
};

// Web Push API
export const webPushApi = {
  /**
   * `topics` narrows what this subscription will receive. Omit for everything.
   * A surface that promised something narrower than "all notifications" passes
   * the topics its promise actually covers — the backend enforces the scope, so
   * the promise and the delivery can't drift apart.
   */
  subscribe: (
    endpoint: string,
    keys: { p256dh: string; auth: string },
    topics?: PushTopic[],
  ) =>
    apiFetch<{ subscriptionId: string }>(
      '/web-push/subscribe',
      { method: 'POST', body: JSON.stringify({ endpoint, keys, ...(topics ? { topics } : {}) }) },
    ),

  unsubscribe: (endpoint: string) =>
    apiFetch<void>(
      '/web-push/unsubscribe',
      { method: 'DELETE', body: JSON.stringify({ endpoint }) },
    ),
};
